import { Close as CloseIcon, Menu as MenuIcon, AdminPanelSettings } from '@mui/icons-material';
import { Box, Drawer, IconButton, useMediaQuery, useTheme, alpha, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Tooltip } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { StarOutline } from '@mui/icons-material';
import { useAdmin } from '../../context/AdminContext';
import { useAuth } from '../../context/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { getIntegrationSetupStateStandalone } from '../../services/user-integrations.service';

import Logo from './Logo';
import MainMenu from './MainMenu';
import StatusIndicator from './StatusIndicator';
import UserMenu from './UserMenu';
import ReferralButton from '../referral/ReferralButton';

const Sidebar: React.FC = () => {
    const theme = useTheme();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [mobileOpen, setMobileOpen] = useState<boolean>(false);
    const { isAdmin } = useAdmin();
    const { currentUser, isNewUser } = useAuth();
    
    // Query integration setup state
    const { data: integrationSetupState } = useQuery({
        queryKey: ['integrationSetupState'],
        queryFn: getIntegrationSetupStateStandalone,
        enabled: !!currentUser && !isNewUser,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
    
    const isOnDisabledPage = (location.pathname === '/integrations' || location.pathname === '/settings');
    const isSideMenuDisabled = integrationSetupState === 'pending' && isOnDisabledPage;

    const handleDrawerToggle = (): void => {
        setMobileOpen(!mobileOpen);
    };

    // Function to navigate to plans page
    const navigateToPlans = () => {
        if (!isSideMenuDisabled) {
            navigate('/plans');
            if (isMobile) {
                handleDrawerToggle();
            }
        }
    };

    // Function to navigate to admin panel
    const navigateToAdmin = () => {
        navigate('/admin');
        if (isMobile) {
            handleDrawerToggle();
        }
    };

    const drawer = (
        <Box sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: (theme) => alpha(theme.palette.background.paper, 0.95),
        }}>
            {/* Drawer header for mobile */}
            {isMobile && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 2,
                        py: 1,
                        borderBottom: '1px solid',
                        borderColor: (theme) => alpha(theme.palette.divider, 0.4),
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Logo />
                        <Box sx={{ ml: 1 }}>
                            <StatusIndicator />
                        </Box>
                    </Box>
                    <IconButton onClick={handleDrawerToggle}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            )}
            {/* Only show logo container on desktop */}
            {!isMobile && (
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '16px',
                }}>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                    }}>
                        <Logo />
                    </Box>
                    <StatusIndicator />
                </Box>
            )}
            <MainMenu 
                onNavigate={isMobile ? handleDrawerToggle : undefined} 
                isDisabled={isSideMenuDisabled}
            />

            {/* Admin menu item - only visible to admins */}
            {isAdmin && (
                <List sx={{ px: 1.5, py: 0, mt: 1, mb: 1 }}>
                    <ListItem disablePadding sx={{ borderRadius: 1, mb: 0.5 }}>
                        <ListItemButton
                            onClick={navigateToAdmin}
                            sx={{
                                borderRadius: 1,
                                py: isMobile ? 1.5 : 1.25,
                                px: 1.5,
                                color: theme.palette.mode === 'light' ? '#212529' : theme.palette.text.secondary,
                                '&:hover': {
                                    bgcolor: theme.palette.primary.main,
                                    color: '#ffffff',
                                },
                            }}
                        >
                            <ListItemIcon
                                sx={{
                                    minWidth: 34,
                                    color: 'inherit',
                                    '& .MuiSvgIcon-root': {
                                        fontSize: '1.4rem',
                                    },
                                }}
                            >
                                <AdminPanelSettings />
                            </ListItemIcon>
                            <ListItemText
                                primary={t('sidebar.menu.admin')}
                                slotProps={{
                                    primary: {
                                        sx: {
                                            fontSize: '0.875rem',
                                            fontWeight: 600,
                                            letterSpacing: '0.01em',
                                        },
                                    },
                                }}
                            />
                        </ListItemButton>
                    </ListItem>
                </List>
            )}

            {/* Packages menu item */}
            <List sx={{ px: 1.5, py: 0, mt: 1, mb: 1 }}>
                {/* Plans button */}
                <Tooltip
                    title={isSideMenuDisabled ? t('sidebar.tooltips.addIntegrationFirst') : ''}
                    placement="right"
                    arrow
                    disableHoverListener={!isSideMenuDisabled}
                >
                    <div>
                        <ListItem disablePadding sx={{ borderRadius: 1, mb: 0.5 }}>
                            <ListItemButton
                                onClick={navigateToPlans}
                                disabled={isSideMenuDisabled}
                                sx={{
                                    borderRadius: 1,
                                    py: isMobile ? 1.5 : 1.25,
                                    px: 1.5,
                                    color: theme.palette.mode === 'light' ? '#212529' : theme.palette.text.secondary,
                                    ...(isSideMenuDisabled && {
                                        color: (theme) => theme.palette.action.disabled,
                                        opacity: 0.6,
                                        cursor: 'not-allowed',
                                        '&:hover': {
                                            bgcolor: 'transparent',
                                            color: (theme) => theme.palette.action.disabled,
                                        },
                                    }),
                                    ...(!isSideMenuDisabled && {
                                        '&:hover': {
                                            bgcolor: theme.palette.primary.main,
                                            color: '#ffffff',
                                        },
                                    }),
                                }}
                            >
                                <ListItemIcon 
                                    sx={{
                                        minWidth: 34,
                                        color: 'inherit',
                                        '& .MuiSvgIcon-root': {
                                            fontSize: '1.4rem',
                                        },
                                    }}
                                >
                                    <StarOutline />
                                </ListItemIcon>
                                <ListItemText
                                    primary={t('sidebar.menu.plans')}
                                    slotProps={{
                                        primary: {
                                            sx: {
                                                fontSize: '0.875rem',
                                                fontWeight: 600,
                                                letterSpacing: '0.01em',
                                            },
                                        },
                                    }}
                                />
                            </ListItemButton>
                        </ListItem>
                    </div>
                </Tooltip>
            </List>
            
            <Box sx={{ flexGrow: 1 }} />
            
            <Box sx={{
                padding: '16px',
                marginTop: 'auto',
                marginBottom: '8px',
            }}>
                <ReferralButton />
                <UserMenu onNavigate={isMobile ? handleDrawerToggle : undefined} />
            </Box>
        </Box>
    );

    return (
        <>
            {/* Mobile header */}
            {isMobile && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 56,
                        px: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        bgcolor: (theme) => alpha(theme.palette.background.paper, 0.95),
                        borderBottom: '1px solid',
                        borderColor: (theme) => alpha(theme.palette.divider, 0.4),
                        zIndex: (theme) => theme.zIndex.appBar,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Logo />
                        <Box sx={{ ml: 1 }}>
                            <StatusIndicator />
                        </Box>
                    </Box>
                    
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{ ml: 'auto' }}
                    >
                        <MenuIcon />
                    </IconButton>
                </Box>
            )}
            <Box component="nav" sx={{
                width: {
                    sm: 230,
                },
                flexShrink: {
                    sm: 0,
                },
            }}>
                <Drawer
                    variant={isMobile ? 'temporary' : 'permanent'}
                    open={isMobile ? mobileOpen : true}
                    onClose={handleDrawerToggle}
                    ModalProps={{
                        keepMounted: true, // Better open performance on mobile.
                    }}
                    sx={{
                        width: 230,
                        display: { xs: 'block' },
                        '& .MuiDrawer-paper': {
                            width: 230,
                            boxSizing: 'border-box',
                            bgcolor: (theme) => alpha(theme.palette.background.paper, 0.95),
                            borderRight: '1px solid',
                            borderColor: theme.palette.divider,
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                            ...(isMobile && {
                                top: 0,
                                height: '100%',
                                zIndex: (theme) => theme.zIndex.drawer,
                            }),
                        },
                    }}
                >
                    {drawer}
                </Drawer>
            </Box>
            {isMobile && <Box sx={{ height: '56px' }} />} {/* Spacer to prevent content from being hidden under the fixed header */}
        </>
    );
};

export default Sidebar;
